// ---- Types ----

export type Suit = "hearts" | "diamonds" | "clubs" | "spades"
export type Value = "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "J" | "Q" | "K" | "A"
export type Phase = "waiting" | "preflop" | "flop" | "turn" | "river" | "showdown"

export interface Card {
  suit: Suit
  value: Value
}

export interface Player {
  userId: string
  displayName: string
  chips: number
  holeCards: Card[]
  bet: number
  folded: boolean
  allIn: boolean
  seatIndex: number
  hasActed: boolean
}

export interface SidePot {
  amount: number
  eligiblePlayerIds: string[]
}

export interface GameState {
  id: string
  phase: Phase
  deck: Card[]
  communityCards: Card[]
  players: Player[]
  pot: number
  sidePots: SidePot[]
  currentPlayerIndex: number
  dealerIndex: number
  smallBlind: number
  bigBlind: number
  minRaise: number
  lastRaiseAmount: number
  currentBet: number
  handNumber: number
}

export type ActionType = "fold" | "check" | "call" | "raise" | "all-in"

export interface PlayerAction {
  type: ActionType
  amount?: number
}

export interface HandRank {
  rank: number // 0 = high card, 1 = pair, ... 9 = royal flush
  name: string
  values: number[] // tiebreaker values in descending order
}

// ---- Constants ----

const SUITS: Suit[] = ["hearts", "diamonds", "clubs", "spades"]
const VALUES: Value[] = ["2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K", "A"]

const VALUE_MAP: Record<Value, number> = {
  "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8,
  "9": 9, "10": 10, "J": 11, "Q": 12, "K": 13, "A": 14,
}

// ---- Deck ----

export function createDeck(): Card[] {
  const deck: Card[] = []
  for (const suit of SUITS) {
    for (const value of VALUES) {
      deck.push({ suit, value })
    }
  }
  return deck
}

export function shuffleDeck(deck: Card[]): Card[] {
  const shuffled = [...deck]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!]
  }
  return shuffled
}

export function cardToString(card: Card): string {
  return `${card.suit}_${card.value}`
}

// ---- Hand Evaluation ----

function getValueNum(v: Value): number {
  return VALUE_MAP[v]
}

function getCombinations(arr: Card[], k: number): Card[][] {
  if (k === 0) return [[]]
  if (arr.length < k) return []
  const [first, ...rest] = arr as [Card, ...Card[]]
  const withFirst = getCombinations(rest, k - 1).map((combo) => [first, ...combo])
  const withoutFirst = getCombinations(rest, k)
  return [...withFirst, ...withoutFirst]
}

function evaluateHand5(cards: Card[]): HandRank {
  const sorted = [...cards].sort((a, b) => getValueNum(b.value) - getValueNum(a.value))
  const vals = sorted.map((c) => getValueNum(c.value))
  const suits = sorted.map((c) => c.suit)

  const isFlush = suits.every((s) => s === suits[0]!)

  // Check straight (including A-2-3-4-5 wheel)
  let isStraight = false
  let straightHigh = 0
  if (
    vals[0]! - vals[1]! === 1 &&
    vals[1]! - vals[2]! === 1 &&
    vals[2]! - vals[3]! === 1 &&
    vals[3]! - vals[4]! === 1
  ) {
    isStraight = true
    straightHigh = vals[0]!
  } else if (
    vals[0] === 14 &&
    vals[1] === 5 &&
    vals[2] === 4 &&
    vals[3] === 3 &&
    vals[4] === 2
  ) {
    // Wheel: A-2-3-4-5
    isStraight = true
    straightHigh = 5
  }

  // Count values
  const counts = new Map<number, number>()
  for (const v of vals) counts.set(v, (counts.get(v) || 0) + 1)

  const groups = Array.from(counts.entries()).sort(
    (a, b) => b[1] - a[1] || b[0] - a[0]
  )

  // Royal flush
  if (isFlush && isStraight && straightHigh === 14) {
    return { rank: 9, name: "Royal Flush", values: [14] }
  }
  // Straight flush
  if (isFlush && isStraight) {
    return { rank: 8, name: "Straight Flush", values: [straightHigh] }
  }
  // Four of a kind
  if (groups[0]![1] === 4) {
    return { rank: 7, name: "Four of a Kind", values: [groups[0]![0], groups[1]![0]] }
  }
  // Full house
  if (groups[0]![1] === 3 && groups[1]![1] === 2) {
    return { rank: 6, name: "Full House", values: [groups[0]![0], groups[1]![0]] }
  }
  // Flush
  if (isFlush) {
    return { rank: 5, name: "Flush", values: vals }
  }
  // Straight
  if (isStraight) {
    return { rank: 4, name: "Straight", values: [straightHigh] }
  }
  // Three of a kind
  if (groups[0]![1] === 3) {
    const kickers = groups.filter((g) => g[1] === 1).map((g) => g[0])
    return { rank: 3, name: "Three of a Kind", values: [groups[0]![0], ...kickers] }
  }
  // Two pair
  if (groups[0]![1] === 2 && groups[1]![1] === 2) {
    const pairs = [groups[0]![0], groups[1]![0]].sort((a, b) => b - a)
    const kicker = groups[2]![0]
    return { rank: 2, name: "Two Pair", values: [...pairs, kicker] }
  }
  // Pair
  if (groups[0]![1] === 2) {
    const kickers = groups.filter((g) => g[1] === 1).map((g) => g[0])
    return { rank: 1, name: "Pair", values: [groups[0]![0], ...kickers] }
  }
  // High card
  return { rank: 0, name: "High Card", values: vals }
}

export function evaluateBestHand(cards: Card[]): HandRank {
  const combos = getCombinations(cards, 5)
  let best: HandRank | null = null
  for (const combo of combos) {
    const rank = evaluateHand5(combo)
    if (!best || compareHands(rank, best) > 0) {
      best = rank
    }
  }
  return best!
}

export function compareHands(a: HandRank, b: HandRank): number {
  if (a.rank !== b.rank) return a.rank - b.rank
  for (let i = 0; i < Math.min(a.values.length, b.values.length); i++) {
    if (a.values[i] !== b.values[i]) return a.values[i]! - b.values[i]!
  }
  return 0
}

// ---- Game State Management ----

export function createInitialGameState(
  id: string,
  players: { userId: string; displayName: string; chips: number; seatIndex: number }[],
  smallBlind: number,
  bigBlind: number,
  dealerIndex: number
): GameState {
  return {
    id,
    phase: "waiting",
    deck: [],
    communityCards: [],
    players: players.map((p) => ({
      ...p,
      holeCards: [],
      bet: 0,
      folded: false,
      allIn: false,
      hasActed: false,
    })),
    pot: 0,
    sidePots: [],
    currentPlayerIndex: -1,
    dealerIndex,
    smallBlind,
    bigBlind,
    minRaise: bigBlind,
    lastRaiseAmount: bigBlind,
    currentBet: 0,
    handNumber: 0,
  }
}

export function startNewHand(state: GameState): GameState {
  const deck = shuffleDeck(createDeck())
  const activePlayers = state.players.filter((p) => p.chips > 0)
  if (activePlayers.length < 2) return state

  // Advance dealer
  const dealerIndex = (state.dealerIndex + 1) % state.players.length
  // Find next active players for blinds
  const sbIndex = findNextActivePlayer(state.players, dealerIndex)
  const bbIndex = findNextActivePlayer(state.players, sbIndex)

  const players: Player[] = state.players.map((p) => ({
    ...p,
    holeCards: [],
    bet: 0,
    folded: p.chips <= 0,
    allIn: false,
    hasActed: false,
  }))

  // Post blinds
  const sbAmount = Math.min(state.smallBlind, players[sbIndex]!.chips)
  players[sbIndex]!.bet = sbAmount
  players[sbIndex]!.chips -= sbAmount
  if (players[sbIndex]!.chips === 0) players[sbIndex]!.allIn = true

  const bbAmount = Math.min(state.bigBlind, players[bbIndex]!.chips)
  players[bbIndex]!.bet = bbAmount
  players[bbIndex]!.chips -= bbAmount
  if (players[bbIndex]!.chips === 0) players[bbIndex]!.allIn = true

  // Deal hole cards
  let deckIndex = 0
  for (const player of players) {
    if (!player.folded) {
      player.holeCards = [deck[deckIndex]!, deck[deckIndex + 1]!]
      deckIndex += 2
    }
  }

  const remainingDeck = deck.slice(deckIndex)
  const pot = sbAmount + bbAmount

  // First to act preflop is the player after big blind
  const firstToAct = findNextActivePlayer(players, bbIndex)

  return {
    ...state,
    phase: "preflop",
    deck: remainingDeck,
    communityCards: [],
    players,
    pot,
    sidePots: [],
    currentPlayerIndex: firstToAct,
    dealerIndex,
    minRaise: state.bigBlind,
    lastRaiseAmount: state.bigBlind,
    currentBet: bbAmount,
    handNumber: state.handNumber + 1,
  }
}

function findNextActivePlayer(players: Player[], fromIndex: number): number {
  let idx = (fromIndex + 1) % players.length
  let loops = 0
  while ((players[idx]!.folded || players[idx]!.chips <= 0) && loops < players.length) {
    // Allow all-in players to not be skipped for blind posting, but skip for action
    if (players[idx]!.allIn || players[idx]!.folded || players[idx]!.chips <= 0) {
      idx = (idx + 1) % players.length
      loops++
    } else {
      break
    }
  }
  return idx
}

function findNextActionablePlayer(players: Player[], fromIndex: number): number {
  let idx = (fromIndex + 1) % players.length
  let loops = 0
  while (loops < players.length) {
    if (!players[idx]!.folded && !players[idx]!.allIn && players[idx]!.chips > 0) {
      return idx
    }
    idx = (idx + 1) % players.length
    loops++
  }
  return -1 // no actionable player
}

export function getActivePlayers(state: GameState): Player[] {
  return state.players.filter((p) => !p.folded)
}

function getActionablePlayers(state: GameState): Player[] {
  return state.players.filter((p) => !p.folded && !p.allIn)
}

// ---- Action Processing ----

export interface ActionResult {
  newState: GameState
  events: PokerEvent[]
}

export type PokerEvent =
  | { type: "player_action"; userId: string; action: ActionType; amount: number }
  | { type: "community_cards"; cards: Card[]; phase: Phase }
  | { type: "showdown"; results: ShowdownResult[] }
  | { type: "pot_update"; pot: number; sidePots: SidePot[] }
  | { type: "turn_change"; userId: string; timeLimit: number }
  | { type: "game_end"; winners: { userId: string; amount: number }[] }
  | { type: "hand_start"; handNumber: number; dealerIndex: number; sbIndex: number; bbIndex: number }

export interface ShowdownResult {
  userId: string
  holeCards: Card[]
  bestHand: HandRank
  won: number
}

export function processAction(
  state: GameState,
  userId: string,
  action: PlayerAction
): ActionResult {
  const playerIndex = state.players.findIndex((p) => p.userId === userId)
  if (playerIndex === -1 || playerIndex !== state.currentPlayerIndex) {
    return { newState: state, events: [] }
  }

  const player = state.players[playerIndex]!
  if (player.folded || player.allIn) {
    return { newState: state, events: [] }
  }

  const events: PokerEvent[] = []
  let newState = { ...state, players: state.players.map((p) => ({ ...p })) }
  const currentPlayer = newState.players[playerIndex]!

  switch (action.type) {
    case "fold": {
      currentPlayer.folded = true
      currentPlayer.hasActed = true
      events.push({ type: "player_action", userId, action: "fold", amount: 0 })

      // Check if only one player remains
      const remaining = getActivePlayers(newState)
      if (remaining.length === 1) {
        // Award pot to last standing
        const winner = remaining[0]!
        winner.chips += newState.pot
        events.push({
          type: "game_end",
          winners: [{ userId: winner.userId, amount: newState.pot }],
        })
        newState.phase = "showdown"
        newState.pot = 0
        return { newState, events }
      }
      break
    }

    case "check": {
      if (newState.currentBet > currentPlayer.bet) {
        // Can't check if there's a bet to match
        return { newState: state, events: [] }
      }
      currentPlayer.hasActed = true
      events.push({ type: "player_action", userId, action: "check", amount: 0 })
      break
    }

    case "call": {
      const callAmount = Math.min(newState.currentBet - currentPlayer.bet, currentPlayer.chips)
      currentPlayer.chips -= callAmount
      currentPlayer.bet += callAmount
      newState.pot += callAmount
      currentPlayer.hasActed = true
      if (currentPlayer.chips === 0) currentPlayer.allIn = true
      events.push({ type: "player_action", userId, action: "call", amount: callAmount })
      events.push({ type: "pot_update", pot: newState.pot, sidePots: newState.sidePots })
      break
    }

    case "raise": {
      const raiseAmount = action.amount || 0
      const totalBet = raiseAmount
      const toAdd = totalBet - currentPlayer.bet
      if (toAdd > currentPlayer.chips || toAdd <= 0) {
        return { newState: state, events: [] }
      }

      // Validate minimum raise
      const raiseOver = totalBet - newState.currentBet
      if (raiseOver < newState.minRaise && toAdd < currentPlayer.chips) {
        // Under-raise only allowed if all-in
        return { newState: state, events: [] }
      }

      currentPlayer.chips -= toAdd
      currentPlayer.bet = totalBet
      newState.pot += toAdd
      newState.lastRaiseAmount = raiseOver
      newState.minRaise = raiseOver
      newState.currentBet = totalBet
      currentPlayer.hasActed = true
      if (currentPlayer.chips === 0) currentPlayer.allIn = true

      // Reset hasActed for other non-folded, non-allin players
      for (const p of newState.players) {
        if (p.userId !== userId && !p.folded && !p.allIn) {
          p.hasActed = false
        }
      }

      events.push({ type: "player_action", userId, action: "raise", amount: totalBet })
      events.push({ type: "pot_update", pot: newState.pot, sidePots: newState.sidePots })
      break
    }

    case "all-in": {
      const allInAmount = currentPlayer.chips
      currentPlayer.bet += allInAmount
      newState.pot += allInAmount
      currentPlayer.chips = 0
      currentPlayer.allIn = true
      currentPlayer.hasActed = true

      if (currentPlayer.bet > newState.currentBet) {
        const raiseOver = currentPlayer.bet - newState.currentBet
        if (raiseOver >= newState.minRaise) {
          newState.lastRaiseAmount = raiseOver
          newState.minRaise = raiseOver
        }
        newState.currentBet = currentPlayer.bet
        // Reset hasActed for others
        for (const p of newState.players) {
          if (p.userId !== userId && !p.folded && !p.allIn) {
            p.hasActed = false
          }
        }
      }

      events.push({ type: "player_action", userId, action: "all-in", amount: allInAmount })
      events.push({ type: "pot_update", pot: newState.pot, sidePots: newState.sidePots })
      break
    }
  }

  // Check if round is over
  const actionable = getActionablePlayers(newState)
  const allActed = actionable.every((p) => p.hasActed)
  const activePlayers = getActivePlayers(newState)

  if (allActed || actionable.length === 0) {
    // Advance phase
    const advanceResult = advancePhase(newState)
    newState = advanceResult.newState
    events.push(...advanceResult.events)
  } else {
    // Move to next player
    const nextIdx = findNextActionablePlayer(newState.players, playerIndex)
    if (nextIdx >= 0) {
      newState.currentPlayerIndex = nextIdx
      events.push({
        type: "turn_change",
        userId: newState.players[nextIdx]!.userId,
        timeLimit: 30,
      })
    }
  }

  return { newState, events }
}

function advancePhase(state: GameState): ActionResult {
  const events: PokerEvent[] = []
  let newState = { ...state, players: state.players.map((p) => ({ ...p })) }

  // Collect bets into pot (already done per action, just reset)
  for (const p of newState.players) {
    p.bet = 0
    p.hasActed = false
  }
  newState.currentBet = 0
  newState.minRaise = newState.bigBlind

  const activePlayers = getActivePlayers(newState)
  const actionable = activePlayers.filter((p) => !p.allIn)

  // If only 1 player not folded or all remaining are all-in, run out the board
  if (activePlayers.length <= 1 || actionable.length <= 1) {
    // Run out remaining community cards
    const runoutResult = runOutBoard(newState)
    return runoutResult
  }

  switch (newState.phase) {
    case "preflop": {
      // Deal flop
      const flop = newState.deck.slice(0, 3)
      newState.deck = newState.deck.slice(3)
      newState.communityCards = flop
      newState.phase = "flop"
      events.push({ type: "community_cards", cards: flop, phase: "flop" })
      break
    }
    case "flop": {
      // Deal turn
      const turnCard = newState.deck[0]!
      newState.deck = newState.deck.slice(1)
      newState.communityCards = [...newState.communityCards, turnCard]
      newState.phase = "turn"
      events.push({ type: "community_cards", cards: [turnCard], phase: "turn" })
      break
    }
    case "turn": {
      // Deal river
      const riverCard = newState.deck[0]!
      newState.deck = newState.deck.slice(1)
      newState.communityCards = [...newState.communityCards, riverCard]
      newState.phase = "river"
      events.push({ type: "community_cards", cards: [riverCard], phase: "river" })
      break
    }
    case "river": {
      // Showdown
      const showdownResult = resolveShowdown(newState)
      return showdownResult
    }
  }

  // Set first to act (after dealer, skipping folded/all-in)
  const firstToAct = findNextActionablePlayer(newState.players, newState.dealerIndex)
  if (firstToAct >= 0) {
    newState.currentPlayerIndex = firstToAct
    events.push({
      type: "turn_change",
      userId: newState.players[firstToAct]!.userId,
      timeLimit: 30,
    })
  }

  return { newState, events }
}

function runOutBoard(state: GameState): ActionResult {
  const events: PokerEvent[] = []
  let newState = { ...state }

  // Deal remaining community cards
  while (newState.communityCards.length < 5 && newState.deck.length > 0) {
    const count = newState.communityCards.length === 0 ? 3 : 1
    const cards = newState.deck.slice(0, count)
    newState.deck = newState.deck.slice(count)
    newState.communityCards = [...newState.communityCards, ...cards]

    const phase =
      newState.communityCards.length === 3 ? "flop" :
      newState.communityCards.length === 4 ? "turn" : "river"
    events.push({ type: "community_cards", cards, phase })
  }

  // Then resolve showdown
  const showdownResult = resolveShowdown(newState)
  events.push(...showdownResult.events)
  return { newState: showdownResult.newState, events }
}

function resolveShowdown(state: GameState): ActionResult {
  const events: PokerEvent[] = []
  let newState = { ...state, players: state.players.map((p) => ({ ...p })) }
  newState.phase = "showdown"

  const activePlayers = getActivePlayers(newState)
  const results: ShowdownResult[] = []

  // Evaluate each active player's best hand
  const playerHands: { player: Player; rank: HandRank }[] = []
  for (const p of activePlayers) {
    const allCards = [...p.holeCards, ...newState.communityCards]
    const best = evaluateBestHand(allCards)
    playerHands.push({ player: p, rank: best })
    results.push({
      userId: p.userId,
      holeCards: p.holeCards,
      bestHand: best,
      won: 0,
    })
  }

  // Sort by hand strength descending
  playerHands.sort((a, b) => compareHands(b.rank, a.rank))

  // Distribute pot (simplified — no side pots for now, handle basic case)
  let remainingPot = newState.pot

  // Group by hand rank (ties)
  const winnerGroup: typeof playerHands = [playerHands[0]!]
  for (let i = 1; i < playerHands.length; i++) {
    if (compareHands(playerHands[i]!.rank, playerHands[0]!.rank) === 0) {
      winnerGroup.push(playerHands[i]!)
    } else {
      break
    }
  }

  const share = Math.floor(remainingPot / winnerGroup.length)
  const remainder = remainingPot - share * winnerGroup.length

  const winners: { userId: string; amount: number }[] = []
  winnerGroup.forEach((wh, i) => {
    const amount = share + (i === 0 ? remainder : 0)
    const playerInState = newState.players.find((p) => p.userId === wh.player.userId)!
    playerInState.chips += amount
    const result = results.find((r) => r.userId === wh.player.userId)!
    result.won = amount
    winners.push({ userId: wh.player.userId, amount })
  })

  newState.pot = 0
  events.push({ type: "showdown", results })
  events.push({ type: "game_end", winners })

  return { newState, events }
}

// ---- Utility for getting valid actions ----

export function getValidActions(state: GameState, userId: string): ActionType[] {
  const player = state.players.find((p) => p.userId === userId)
  if (!player || player.folded || player.allIn) return []
  if (state.players[state.currentPlayerIndex]?.userId !== userId) return []

  const actions: ActionType[] = ["fold"]

  if (state.currentBet === player.bet) {
    actions.push("check")
  } else {
    actions.push("call")
  }

  if (player.chips > 0) {
    const callCost = state.currentBet - player.bet
    if (player.chips > callCost) {
      actions.push("raise")
    }
    actions.push("all-in")
  }

  return actions
}

export function getCallAmount(state: GameState, userId: string): number {
  const player = state.players.find((p) => p.userId === userId)
  if (!player) return 0
  return Math.min(state.currentBet - player.bet, player.chips)
}

export function getMinRaise(state: GameState): number {
  return state.currentBet + state.minRaise
}

export function getMaxRaise(state: GameState, userId: string): number {
  const player = state.players.find((p) => p.userId === userId)
  if (!player) return 0
  return player.chips + player.bet
}

// ---- Bot Hand Strength Evaluation ----

/**
 * Evaluate hand strength as a 0-100 score.
 * Uses hole cards + community cards to determine relative strength.
 * Pre-flop: based on starting hand strength tables.
 * Post-flop: based on best 5-card hand rank normalized to 0-100.
 */
export function evaluateHandStrength(holeCards: Card[], communityCards: Card[]): number {
  if (communityCards.length === 0) {
    // Pre-flop: evaluate starting hand strength
    return evaluatePreflopStrength(holeCards)
  }
  
  // Post-flop: evaluate best hand from available cards
  const allCards = [...holeCards, ...communityCards]
  const best = evaluateBestHand(allCards)
  
  // Normalize rank (0-9) to 0-100 scale with sub-ranking
  // rank 0 (high card) = 5-15, rank 9 (royal flush) = 95-100
  const baseScore = best.rank * 10 + 5
  
  // Add tiebreaker value (0-9 range based on kicker strength)
  const kickerBonus = best.values[0] ? Math.min(9, Math.round((best.values[0] / 14) * 9)) : 0
  
  return Math.min(100, baseScore + kickerBonus)
}

function evaluatePreflopStrength(holeCards: Card[]): number {
  if (holeCards.length < 2) return 30
  
  const v1 = VALUE_MAP[holeCards[0]!.value]
  const v2 = VALUE_MAP[holeCards[1]!.value]
  const high = Math.max(v1, v2)
  const low = Math.min(v1, v2)
  const paired = v1 === v2
  const suited = holeCards[0]!.suit === holeCards[1]!.suit
  
  // Pocket pairs
  if (paired) {
    if (high >= 13) return 85  // KK, AA
    if (high >= 10) return 75  // TT, JJ, QQ
    if (high >= 7) return 60   // 77-99
    return 45                   // 22-66
  }
  
  // Connected/suited high cards
  const gap = high - low
  let score = 0
  
  // Base on high card value
  score = Math.round((high / 14) * 50) + 10
  
  // Suited bonus
  if (suited) score += 8
  
  // Connectivity bonus (closer = better)
  if (gap === 1) score += 6      // Connectors
  else if (gap === 2) score += 3  // One-gappers
  
  // Premium hands boost
  if (high === 14 && low >= 10) score = Math.max(score, 70)  // AT+
  if (high === 14 && low >= 12) score = Math.max(score, 78)  // AQ+
  if (high === 13 && low >= 11) score = Math.max(score, 68)  // KJ+
  
  // Trash hands penalty
  if (high <= 9 && gap >= 4 && !suited) score = Math.min(score, 20)
  
  return Math.max(5, Math.min(90, score))
}
